import { ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLang } from "@/i18n";

const BASE = "https://connected-center-basra.aglowspark17.chatgpt.site";

const WHATSAPP_SAMPLES =
  "https://wa.me/9647760506057?text=%D9%85%D8%B1%D8%AD%D8%A8%D9%8B%D8%A7%20%D9%83%D9%88%D9%86%D9%83%D8%AA%D8%AF%D8%8C%20%D8%A3%D8%B1%D8%BA%D8%A8%20%D8%A8%D9%85%D8%B4%D8%A7%D9%87%D8%AF%D8%A9%20%D8%B9%D9%8A%D9%86%D8%A7%D8%AA%20%D8%A3%D9%84%D9%88%D8%A7%D9%86%20Aegis%20%D9%88%D8%A7%D8%AE%D8%AA%D9%8A%D8%A7%D8%B1%20%D9%84%D9%88%D9%86%20%D9%84%D8%B3%D9%8A%D8%A7%D8%B1%D8%AA%D9%8A";

const FINISHES = [
  { id: "finish-gloss", num: "01", en: "GLOSS", image: `${BASE}/color-studio/finish-gloss.webp` },
  { id: "finish-matte", num: "02", en: "MATTE", image: `${BASE}/color-studio/finish-matte.webp` },
  { id: "finish-color", num: "03", en: "COLOR", image: `${BASE}/color-studio/5505.webp` },
];

export default function ColorStudio() {
  const { t, lang } = useLang();
  const ArrowIcon = lang === "ar" ? ArrowLeft : ArrowRight;
  return (
    <section
      id="colors"
      data-testid="color-studio-section"
      className="relative overflow-hidden bg-[#0E0E0E]/40 py-24 lg:py-32"
    >
      <div
        className="pointer-events-none absolute -bottom-40 start-0 h-[28rem] w-[28rem] rounded-full bg-[#E3AF4F]/10 blur-[120px]"
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-end gap-10 lg:grid-cols-2">
          <Reveal>
            <p className="mb-3 flex items-center gap-3 text-xs font-bold tracking-[0.25em] text-[#E3AF4F]">
              <span className="h-px w-10 bg-[#E3AF4F]" />
              {t.colors.eyebrow}
            </p>
            <h2 className="font-heading text-2xl font-bold leading-snug text-[#F5F2EB] sm:text-3xl lg:text-4xl">
              {t.colors.headingA} <span className="text-gold-grad">{t.colors.headingB}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-lg text-base leading-relaxed text-[#A8A29E] lg:ms-auto">
              {t.colors.paragraph}
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-3">
          {FINISHES.map((f, i) => (
            <Reveal key={f.id} delay={i * 0.1}>
              <article
                data-testid={`finish-card-${f.id}`}
                className="group relative overflow-hidden rounded-3xl border border-[#F5F2EB]/10 transition-all duration-500 hover:-translate-y-1 hover:border-[#E3AF4F]/50"
              >
                <img
                  src={f.image}
                  alt={`AEGIS — ${f.en}`}
                  loading="lazy"
                  className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#000000]/90 via-transparent to-transparent" />
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-6">
                  <div>
                    <p className="text-[11px] font-bold tracking-[0.25em] text-[#E3AF4F]">
                      {f.num} / {f.en}
                    </p>
                    <h3 className="mt-1 font-heading text-xl font-bold text-white">
                      {t.colors.finishes[i]}
                    </h3>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15} className="mt-10 flex flex-wrap gap-4">
          <a
            href={WHATSAPP_SAMPLES}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="color-studio-samples-button"
            className="group inline-flex items-center gap-2 rounded-full bg-[#E3AF4F] px-8 py-3.5 text-sm font-bold text-[#000000] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#F5D08C] hover:shadow-lg hover:shadow-[#E3AF4F]/25"
          >
            {t.colors.button}
            <ArrowIcon
              size={16}
              className={`transition-transform duration-300 ${
                lang === "ar" ? "group-hover:-translate-x-1" : "group-hover:translate-x-1"
              }`}
            />
          </a>
        </Reveal>
      </div>
    </section>
  );
}
